'use client'

import Link from 'next/link'
import { Section, SectionHeading } from '@/components/platform/v2/Section'
import { ProductHero, Statement, FlowStrip, FeatureCard, NextSteps, ProductCta, RelatedCases } from '@/components/platform/v2/ProductKit'
import { PhoneMock, MockRow } from '@/components/platform/v2/Mocks'

const SCENARIOS = [
    { title: 'Корпоративный сайт', text: 'Компания, услуги, доверие. Заявки сразу в CRM.' },
    { title: 'Продающий сайт', text: 'Одна задача: получить обращение и передать его менеджеру.' },
    { title: 'Каталог', text: 'Товары и услуги из платформы, без ручного дублирования.' },
    { title: 'E-commerce', text: 'Покупка, оплата и заказ в общей системе с приложением.' },
    { title: 'Сайт мероприятия', text: 'Программа, регистрация, участники, билеты.' },
    { title: 'Сайт сообщества', text: 'Участники, материалы, вход в закрытую часть.' },
    { title: 'Личный кабинет', text: 'Клиент видит свои заказы, документы и историю.' },
    { title: 'Сервисный портал', text: 'Обращения, статусы, поддержка внутри контура.' },
]

const WebsitesPage = () => {
    return (
        <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
            <ProductHero
                eyebrow="Сайты"
                titleLines={['Не просто сайт.', 'Часть вашей бизнес-системы.']}
                lead="Полноценный сайт с конструктором и общей CRM можно выбрать уже в PRO — стандартное приложение BASE сохраняется. PRO+ и GENIUS дают расширенное приложение и сайт одновременно."
                primary={{ label: 'Собрать сайт под мой бизнес', to: '/signin?focus=website' }}
                secondary={{ label: 'Обзор платформы', to: '/platform' }}
                visual={
                    <img
                        src={'/assets/images/platform/websites-mascot-hero.png'}
                        alt="Маскот VCENTRE.IT с планшетом и макетом сайта"
                        width={1122}
                        height={1402}
                        loading="eager"
                        className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
                    />
                }
            />

            <Section tone="quiet">
                <div className="flex flex-col gap-8">
                    <div className="grid gap-6 lg:grid-cols-2 lg:items-end">
                        <div className="flex flex-col gap-4">
                            <p className="eyebrow">Главная мысль</p>
                            <h2 className="display-2">
                                Большинство сайтов заканчиваются словами
                                <br />
                                <span className="text-muted-foreground">«Спасибо, ваша заявка отправлена».</span>
                            </h2>
                        </div>
                        <p className="lede">
                            Здесь всё только начинается: заявка становится клиентом, у клиента появляется ответственный, а у менеджера —
                            задача и вся история обращения.
                        </p>
                    </div>
                    <FlowStrip
                        steps={[
                            { title: 'Сайт' },
                            { title: 'Форма' },
                            { title: 'Клиент' },
                            { title: 'CRM', accent: true },
                            { title: 'Менеджер' },
                            { title: 'Сделка' },
                            { title: 'Автоматизация' },
                        ]}
                        compact
                    />
                </div>
            </Section>

            {/* АДМИНКА */}
            <Section>
                <SectionHeading
                    align="left"
                    eyebrow="Своя админка"
                    title="Основным содержимым сайта управляет сам бизнес."
                    text="Тексты, цены, баннеры и каталог можно обновлять в своей административной части. Нестандартные изменения оцениваются отдельно."
                />
                <div className="mt-8 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                    <div className="grid gap-3 sm:grid-cols-2">
                        {['Страницы', 'Контент и тексты', 'Каталог', 'Услуги', 'Баннеры', 'Формы', 'Заявки', 'Другие сущности проекта'].map(
                            (i) => (
                                <div
                                    key={i}
                                    className="rounded-xl border border-border/60 bg-[hsl(var(--deep-black))] px-4 py-3 text-sm font-semibold text-foreground/90"
                                >
                                    {i}
                                </div>
                            ),
                        )}
                    </div>
                    <div className="surface flex flex-col gap-3">
                        <p className="text-lg font-extrabold text-foreground">Набор зависит от проекта</p>
                        <p className="body-text">
                            Конструктор, доступные расширения и общие данные платформы помогают собрать сайт под задачу бизнеса: услуги
                            и специалисты, товары и каталог, программа и участники мероприятия. Индивидуальная разработка оценивается отдельно.
                        </p>
                    </div>
                </div>
            </Section>

            {/* САЙТ + ПРИЛОЖЕНИЕ */}
            <Section tone="quiet">
                <div className="grid gap-10 lg:grid-cols-[1fr_0.8fr] lg:items-center">
                    <div className="flex flex-col gap-5">
                        <p className="eyebrow">PRO+ и GENIUS · два расширенных канала</p>
                        <h2 className="display-2">
                            Сайт привлекает.
                            <br />
                            Приложение удерживает.
                        </h2>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <FeatureCard title="Сайт" text="Первый контакт, поиск, доверие, заявка." />
                            <FeatureCard title="Приложение" text="Повторные покупки, бонусы, чат, история." />
                            <FeatureCard title="CRM" text="Сохраняет историю и связывает оба канала." />
                            <FeatureCard title="Автоматизация" text="Продолжает работу после обращения." />
                        </div>
                    </div>
                    <PhoneMock title="Тот же клиент">
                        <MockRow label="Пришёл с сайта" accent />
                        <MockRow label="Заявка №218" value="в CRM" />
                        <MockRow label="Установил приложение" />
                        <MockRow label="Профиль связан" value="одна история" accent />
                        <MockRow label="Повторный заказ" value="через 3 недели" />
                    </PhoneMock>
                </div>
                <div className="mt-8 text-center">
                    <Link href="/signin?focus=website-app" className="btn-link">
                        Собрать сайт и приложение вместе
                    </Link>
                </div>
            </Section>

            {/* СЦЕНАРИИ */}
            <Section>
                <SectionHeading
                    eyebrow="Сценарии"
                    title="Сайт под задачу, а не под шаблон"
                    text="Мы начинаем не с типа дизайна, а с того, что сайт должен делать для бизнеса."
                />
                <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {SCENARIOS.map((s) => (
                        <div key={s.title} className="rounded-2xl border border-border/60 bg-[hsl(var(--deep-black))] p-5">
                            <p className="text-sm font-bold text-foreground">{s.title}</p>
                            <p className="mt-1.5 text-sm text-muted-foreground">{s.text}</p>
                        </div>
                    ))}
                </div>
            </Section>

            <Section tone="quiet">
                <Statement note="Сайт уже есть? Не обязательно делать новый.">
                    Сайт — это вход в вашу систему,
                    <br />
                    <span className="text-primary">а не отдельная страница в интернете.</span>
                </Statement>
            </Section>

            <Section>
                <RelatedCases
                    slugs={['piter', 'business-forum']}
                    text="Проекты, где сайт связан с платформой: каталогом, заявками и клиентами."
                />
            </Section>

            <Section tone="quiet">
                <NextSteps
                    items={[
                        { to: '/platform/promotion', label: 'Продвижение', text: 'Теперь приведём туда клиентов' },
                        { to: '/platform/crm', text: 'Посмотрите, куда попадают заявки с сайта' },
                    ]}
                />
            </Section>

            <Section>
                <ProductCta
                    title="Покажите, как сейчас работает ваш бизнес"
                    text="Выберите сайт как расширенный канал в PRO или объедините его с расширенным приложением в PRO+. Клиенты, каталог и CRM остаются общими."
                    buttonLabel="Собрать концепт сайта"
                    to="/signin?focus=website&source=websites-bottom"
                    secondary={{ to: '/platform/integrations', label: 'Интеграции' }}
                    visual={
                        <img
                            src={'/assets/images/platform/websites-mascot-cta.png'}
                            alt="Маскот VCENTRE.IT с ракетой и фразой «Запускаем быстро»"
                            width={1254}
                            height={1254}
                            loading="lazy"
                            className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
                        />
                    }
                />
            </Section>
        </div>
    )
}

export default WebsitesPage
